import React from 'react'

import {isNil} from 'es-toolkit'

import './labeled-checkbox-field.scss'

import useUniqueId from '../../hooks/use-unique-id'
import cn from '../../libs/class-names'

import {CommonProps, Overwrite} from '../../types'

import CheckboxField from '../checkbox-field'

type Props<T extends React.ElementType> = Overwrite<
  Omit<CommonProps<T>, 'onChange' | 'onBlur'>,
  {
    as?: T
    id?: string
    name?: string
    checked?: boolean
    label: React.ReactNode
    inline?: boolean
    size?: '100' | '90'
    appearance?: 'base' | 'error'
    partial?: boolean
    disabled?: boolean
    count?: number | string
    suffix?: React.ReactNode
    onChange?: (
      value: boolean,
      name: string | undefined,
      event: React.ChangeEvent<HTMLInputElement>
    ) => void
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  }
>

const LabeledCheckbox = <T extends React.ElementType = 'div'>({
  as,
  className,
  id,
  checked,
  label,
  inline = false,
  size = '100',
  appearance = 'base',
  partial = false,
  disabled = false,
  count,
  suffix,
  onChange,
  onBlur,
  name,
  ...props
}: Props<T>) => {
  const Root = (as ?? 'div') as React.ElementType

  const actualId = useUniqueId(id)

  return (
    <Root
      {...props}
      className={cn(
        'LabeledCheckbox',
        {
          [`size-${size}`]: true,
          [`appearance-${appearance}`]: !!appearance,
          inline,
          disabled,
        },
        className
      )}
    >
      <CheckboxField
        name={name}
        appearance={appearance}
        id={actualId}
        checked={checked}
        disabled={disabled}
        partial={partial}
        onChange={onChange}
        onBlur={onBlur}
      />
      <div className="LabeledCheckbox__content">
        <label htmlFor={actualId} className="LabeledCheckbox__label">
          {label}
        </label>
        {!isNil(count) && <span className="LabeledCheckbox__count">({count})</span>}
      </div>
      {suffix && <div className="LabeledCheckbox__suffix">{suffix}</div>}
    </Root>
  )
}

export default LabeledCheckbox
